import axios from 'axios'
import qs from 'qs'
import addMocks from 'api/mocks'
import extractErrors from 'utils/extractErrors'
import { store } from 'store'
import RefreshToken from 'api/refreshPromise'
import { notificationShow } from 'modules/Notifications/actions'
import { LOGOUT } from 'modules/Auth/types'

class Api {
  constructor(config) {
    if (process.env.NODE_ENV !== 'production') {
      addMocks(axios)
    }

    this.instance = axios.create({
      ...config,
      withCredentials: true,
    })

    this.instance.interceptors.request.use((config) => {
      const switchedUser = localStorage.getItem('su')

      if (switchedUser) {
        config.headers['X-Switch-User'] = switchedUser
      }

      const token = localStorage.getItem('access_token')
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }

      return config
    })

    this.instance.interceptors.response.use(
      // в случае валидного accessToken ничего не делаем:
      (response) => {
        return response
      },
      async (error) => {
        const originalRequest = { ...error.config }

        if (
          // проверим, что ошибка именно из-за невалидного accessToken
          error?.response?.status === 401 &&
          error.config &&
          !originalRequest.url.match(/(\/logout$)|(\/auth$)|(\/auth\/two_factor)|(admitad\/check)/)
        ) {
          try {
            await RefreshToken.getInstance()
            return this.instance.request(originalRequest)
          } catch (error) {
            store.dispatch((() => ({ type: LOGOUT }))())
          }
        }

        throw error
      },
    )

    this.get = this.get.bind(this)
    this.put = this.put.bind(this)
    this.patch = this.patch.bind(this)
    this.post = this.post.bind(this)
    this.delete = this.delete.bind(this)
  }

  request(config) {
    return this.instance
      .request(config)
      .then((response) => {
        if (process.env.NODE_ENV !== 'production' && localStorage.getItem('log_xhr') === 'true') {
          const url_mask = localStorage.getItem('log_xhr_url_mask')

          if (!url_mask || (!!url_mask && new RegExp(url_mask).test(response.config.url))) {
            console.log('------START REQUEST----------')
            console.log(
              `Request method: %c${response.config.method.toUpperCase()}, %curl: %c${
                response.config.baseURL + response.config.url
              }`,
              'color: blue; font-weight: bold;',
              'color: black',
              'color: red; font-weight: bold;',
            )

            console.log('Request params: \n \n', JSON.stringify(response.config.params, null, 2))
            console.log(
              'Request payload data: \n \n',

              response.config?.payload ?? response.config?.data
                ? JSON.stringify(
                    JSON.parse(response.config?.payload ?? response.config?.data),
                    null,
                    2,
                  )
                : '',
            )
            console.log('Success response data: ', { ...response.data })
            console.groupCollapsed('Response as string (click me to uncollapse) =>') // S
            console.log(JSON.stringify(response.data, null, 2))
            console.groupEnd()
            console.log('Full response: ', { ...response })
            console.log('--------END REQUEST-----------')
            console.log('  ')
          }
        }

        return {
          response: {
            ...response.data,
            status: response.status,
            statusText: response.statusText,
          },
        }
      })
      .catch((error) => {
        if (axios.isCancel(error)) {
          return {
            error: {
              canceled: true,
              status: 499,
              statusText: error.message,
            },
          }
        } else {
          if (process.env.NODE_ENV !== 'production' && localStorage.getItem('log_xhr') === 'true') {
            try {
              console.log('Response error: ', error.toJSON())
            } catch (e) {
              console.log('Response error: ', error)
            }
          }

          if (error?.response?.status >= 500) {
            const { hash = null } = extractErrors(error.response?.data?.errors)

            !!hash &&
              store.dispatch(
                notificationShow({
                  type: 'warning',
                  message: hash,
                  delay: 20000,
                }),
              )

            return {
              error: {
                status: error.response.status,
                statusText: error.message,
                errors: error.response?.data?.errors ?? [],
              },
            }
          }

          if (error.message === 'Network Error') {
            return {
              error: {
                status: 400,
                statusText: error.message + '\nurl: ' + error.config.url,
              },
            }
          }

          if (error.code === 'ECONNABORTED' || error.response.status === 408) {
            return {
              error: {
                status: 408,
                statusText: error.message,
              },
            }
          }

          if (error?.response?.status === 413) {
            return {
              error: {
                status: 413,
                statusText: error.response.statusText,
                errors: ['request_entity_too_large'],
              },
            }
          }

          const data = error.response.data.data || error.response.data

          return {
            error: {
              ...data,
              status: error.response.status,
              statusText: error.response.statusText,
            },
          }
        }
      })
  }

  get(url, payload = {}) {
    const config = {
      ...payload,
      method: 'GET',
      url,
    }

    if (payload.params) {
      config.paramsSerializer = (params) => {
        return qs.stringify(params, { arrayFormat: 'brackets' })
      }
    }

    return this.request(config)
  }

  put(url, payload = {}) {
    return this.request({
      method: 'PUT',
      url,
      ...payload,
    })
  }

  post(url, payload = {}) {
    return this.request({
      ...payload,
      method: 'POST',
      url,
    })
  }

  patch(url, payload = {}) {
    return this.request({
      ...payload,
      method: 'PATCH',
      url,
    })
  }

  delete(url, payload = {}) {
    return this.request({
      ...payload,
      method: 'DELETE',
      url,
    })
  }
}

export default Api
